import { splitRecordViewPath } from "@/components/views/views-data";
import { parseAppHref } from "@/lib/activity-links";
import { parseEventPath } from "@/lib/event-path";
import {
	agentScope,
	isEntityPath,
	isEntityTypeSegment,
} from "@/lib/peek-search";
import { decodeEntityTypeParam } from "@/lib/url";

/**
 * Resolve a clicked activity/notification href into a structured peek
 * destination so the pane can mount the right shared content component. The
 * dedicated pages parse the same strings from their own URLs.
 *
 * Peekable destinations render their dedicated-page content component inline in
 * the pane (automation runs, memory events/approvals). Connector detail is a full
 * management surface and doesn't fit a 460px pane, so it navigates normally
 * (the caller falls back to navigate() when this returns null).
 */
type PeekDestination =
	| {
			kind: "automation";
			owner: string;
			automationId: string;
			view?: "activity" | "definition";
			runId?: number;
	  }
	| {
			kind: "memory";
			owner: string;
			runId: number;
	  }
	| {
			kind: "memory";
			owner: string;
			runIds: number[];
	  }
	| {
			kind: "memory";
			owner: string;
			contentIds: number[];
	  }
	| {
			kind: "entity";
			owner: string;
			/** `<type>/<slug>[/…]`, segments still percent-encoded. */
			path: string;
	  }
	| {
			/** One entity type (`/{owner}/events?type=<type>[&view=activity]`). */
			kind: "entity-type";
			owner: string;
			typeSlug: string;
			view?: "activity";
	  }
	| {
			/** One of a record's views, fullscreen
			 *  (`/{owner}/<type>/<slug>/-/views/<key>`). */
			kind: "record-view";
			owner: string;
			/** `<type>/<slug>[/…]`, segments still percent-encoded. */
			path: string;
			viewKey: string;
	  }
	| {
			/** One agent's or connected app's activity
			 *  (`/{owner}/events?agent=<id>` or `?client_ids=<ids>`). */
			kind: "agent";
			owner: string;
			agentId?: string;
			clientIds?: string[];
	  }
	| {
			/** One event (`/events/<id>`), optionally on one of the views that
			 *  render it (`/events/<id>/-/views/<key>`). */
			kind: "event";
			owner: string;
			eventId: number;
			viewKey?: string;
	  };

const SEG = "[^/]+";

const AUTOMATION_RE = new RegExp(`^/(${SEG})/automations/(${SEG})/?$`);

// Run permalinks live on /events; stored notification links from before the
// Activity/Memory split still say /memory, so both shapes stay peekable.
const RUN_STREAM_RE = new RegExp(`^/(${SEG})/(?:events|memory)/?$`);

export function parsePeekHref(href: string): PeekDestination | null {
	const { pathname, search } = parseAppHref(href);

	const m = pathname.match(AUTOMATION_RE);
	if (m) {
		const [, owner, automationId] = m;
		const view =
			search?.view === "activity" || search?.view === "definition"
				? search.view
				: undefined;
		const runId = parseRunId(search?.run_id);
		return { kind: "automation", owner, automationId, view, runId };
	}
	const mm = pathname.match(RUN_STREAM_RE);
	if (mm) {
		const [, owner] = mm;
		const runIds = parseIdList(search?.run_ids);
		if (runIds?.length === 1) {
			return { kind: "memory", owner, runId: runIds[0] };
		}
		if (runIds && runIds.length > 1) {
			return { kind: "memory", owner, runIds };
		}
		const contentIds = parseIdList(search?.content_ids);
		if (contentIds) return { kind: "memory", owner, contentIds };
		const type = search?.type?.trim();
		if (type && isEntityTypeSegment(type)) {
			return {
				kind: "entity-type",
				owner,
				typeSlug: decodeEntityTypeParam(type),
				view: search?.view === "activity" ? "activity" : undefined,
			};
		}
		const agent = agentScope(search);
		if (agent) return { kind: "agent", owner, ...agent };
		return null;
	}

	// One event's own page, before the record forms: `/events/<id>/-/views/<key>`
	// is pair-wise too, and would otherwise read as a record path.
	const event = parseEventPath(pathname);
	if (event) return { kind: "event", ...event };

	// A single record (`/{owner}/<type>/<slug>[/…]`). Peekable so a record can
	// show its own activity beside itself instead of navigating away. The
	// full-width form (`/{owner}/events/<type>/<slug>`) parses to the same
	// destination — one record, two widths.
	// Pair-wise in both forms (`length % 2` differs by the extra `events`
	// segment): a half-written path addresses no record.
	const segments = pathname.split("/").filter(Boolean);
	if (segments.length >= 4 && segments[1] === "events") {
		return isEntityPath(segments.slice(2).join("/"))
			? {
					kind: "entity",
					owner: segments[0],
					path: segments.slice(2).join("/"),
				}
			: null;
	}
	const split =
		segments.length >= 3
			? splitRecordViewPath(segments.slice(1).join("/"))
			: null;
	if (split && isEntityPath(split.entityPath)) {
		return split.viewKey
			? {
					kind: "record-view",
					owner: segments[0],
					path: split.entityPath,
					viewKey: split.viewKey,
				}
			: { kind: "entity", owner: segments[0], path: split.entityPath };
	}

	return null;
}

function parseIdList(value: string | undefined): number[] | null {
	if (!value) return null;
	const parts = value.split(",");
	if (parts.length === 0 || parts.length > 50) return null;
	const ids = parts.map((part) => parseRunId(part.trim()));
	return ids.every((id): id is number => id != null) ? ids : null;
}

function parseRunId(value: string | undefined): number | undefined {
	if (value == null || value === "") return undefined;
	const n = Number(value);
	return Number.isFinite(n) && n > 0 ? n : undefined;
}
